import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { FullPageSpinner } from '@/components/common/Spinner';
import { ROUTES } from './routes';

/**
 * Gate for authenticated routes.
 *
 * Waits for the initial session restore before deciding. Redirecting while that
 * is still in flight would bounce a signed-in user to the login screen on every
 * page refresh.
 */
export function ProtectedRoute({ permissions = [] }: { permissions?: string[] }) {
  const { isAuthenticated, initialising, canAny } = useAuth();
  const location = useLocation();

  if (initialising) {
    return <FullPageSpinner label="Restoring your session" />;
  }

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there.
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  if (!canAny(permissions)) {
    return <Navigate to={ROUTES.forbidden} replace />;
  }

  return <Outlet />;
}

/** Keeps a signed-in user from landing back on the login screen. */
export function PublicOnlyRoute() {
  const { isAuthenticated, initialising } = useAuth();

  if (initialising) {
    return <FullPageSpinner label="Loading" />;
  }
  return isAuthenticated ? <Navigate to={ROUTES.dashboard} replace /> : <Outlet />;
}
